import { location } from 'cdui-js';
import { certificateApply, findCertDown, themeAcquire } from '../../../../../../api/open-account';
import { consoleLog } from '../../../../../../lib/utils';
import { showToast } from '../../../../components/Toast';
import i18n from '../../../../../../i18n';
import { pState } from '../../store';
import { persistMainlandOpenAccountToken } from '../../openAccountSession';
import {
  buildSzcaH5ReturnUrl,
  isSzcaReturnFromQuery,
  type SzcaH5ReturnContext,
} from './openSzcaAuthViaApp';
import {
  captureCertificateSerialFromQuery,
  clearSzcaPendingState,
  hasSzcaPendingSession,
  hydrateSzcaCertToPState,
  loadSzcaPendingState,
  persistSzcaCertToStore,
  restoreKeyPairToPState,
  saveSzcaPendingState,
} from './szca-face';

/** node-forge 独立 chunk；勿静态 import lib/cert */
const loadCert = () => import('../../phases/open-cert');

export type SzcaAuthContext = SzcaH5ReturnContext;

export type RunSzcaAuthFlowOptions = {
  context?: SzcaAuthContext;
  /** 忽略已有 certSn，强制重新申请证书并做人脸 */
  force?: boolean;
};

export type TryCompleteSzcaReturnOptions = RunSzcaAuthFlowOptions & {
  onSuccess?: () => void | Promise<void>;
};

type SzcaPrepareResult = {
  authUrl: string;
  certDn: string;
  bizNo?: string;
  resId?: string;
};

type SzcaReturnResult = 'ok' | 'need_reapply' | 'failed';

/** findCertDown 返回认证进行中，需重新 certificateApply */
const SZCA_AUTH_IN_PROGRESS_RETCODE = '-5';

/** 证书就绪后写入 pState + localStorage（签名页复用） */
const persistSzcaCert = (certDn: string, certSn: string, pems?: { privateKeyPem?: string; publicKeyPem?: string }) => {
  persistSzcaCertToStore(pState, certDn, certSn, pems);
};

/**
 * 整页回跳后 pState.id 可能仍为空。
 * 优先内存里的申请单 id，否则用跳走前写入 pending 的 busId，并回填 pState.id。
 */
const resolveSzcaBusId = () => {
  const fromState = String(pState.id || '');
  if (fromState) return fromState;
  const fromPending = String(loadSzcaPendingState()?.busId || '');
  if (fromPending) pState.id = fromPending;
  return fromPending;
};

/** 调用 certificateApply 并写入 pending */
const applySzcaCertificate = async (
  certDn: string,
  keyPair: unknown,
  context: SzcaAuthContext,
): Promise<SzcaPrepareResult | null> => {
  pState.certDn = certDn;
  pState.keyPair = keyPair;

  const cert = await loadCert();
  const subject = cert.formatSubject(certDn);
  const certP10 = cert.createPKCS10(pState.keyPair, subject);
  consoleLog(certP10);

  const busId = resolveSzcaBusId();
  if (!busId) {
    showToast(i18n.OpenProgress.Risk.Szca.ToastSessionExpired);
    return null;
  }

  //CA人脸完成后回跳H5
  const videoCallbackBusUrl = buildSzcaH5ReturnUrl({ context });
  consoleLog('[SZCA] certificateApply videoCallbackBusUrl', { videoCallbackBusUrl, certDn, context, busId }, 'all');

  const data = (await certificateApply(certDn, certP10, '', '', busId, videoCallbackBusUrl)) as {
    authUrl?: string;
    certSn?: string | null;
    bizNo?: string;
    resId?: string;
    retCode?: string;
    descInfo?: string;
  };
  consoleLog('[SZCA] certificateApply response', data, 'all');

  if (data?.retCode != null && String(data.retCode) !== '0') {
    showToast(data.descInfo || i18n.OpenProgress.Risk.Video.ToastServiceError);
    return null;
  }

  if (!data?.authUrl) {
    showToast(i18n.OpenProgress.Risk.Szca.ToastAuthUrlRequired);
    return null;
  }

  // bizNo/resId 必须与 certificateApply 返回值一致，后续 findCertDown 原样带回
  const bizNo = data.bizNo;
  const { privateKeyPem, publicKeyPem } = cert.exportKeyPairPem(
    keyPair as Parameters<typeof cert.exportKeyPairPem>[0],
  );
  saveSzcaPendingState({
    busId,
    certDn,
    bizNo,
    resId: data.resId,
    privateKeyPem,
    publicKeyPem,
    context: undefined,
  });

  // certificateApply 若已带回 certSn，立即落盘，避免整页跳转后丢失
  if (data.certSn) {
    persistSzcaCert(certDn, String(data.certSn), { privateKeyPem, publicKeyPem });
  }

  return {
    authUrl: data.authUrl,
    certDn,
    bizNo,
    resId: data.resId,
  };
};

/** themeAcquire + 新密钥对 + certificateApply */
const prepareSzcaFaceAuth = async (
  context: SzcaAuthContext,
  force = false,
): Promise<SzcaPrepareResult | null> => {
  if (!force && pState.certSn) return null;

  const themeData = await themeAcquire(pState.uploadLicForm.idCardNo, pState.uploadLicForm.fullName);

  if (!themeData || !themeData.certDN) {
    showToast(i18n.OpenProgress.Risk.Video.ToastServiceError);
    return null;
  }

  const { createKeypair } = await loadCert();
  const keyPair = (await createKeypair(2048)) as unknown;
  return applySzcaCertificate(themeData.certDN, keyPair, context);
};

/** findCertDown 返回认证进行中时，复用 pending 密钥重新 certificateApply */
const retrySzcaCertificateApply = async (context: SzcaAuthContext): Promise<SzcaPrepareResult | null> => {
  const pending = loadSzcaPendingState();
  if (!pending) {
    showToast(i18n.OpenProgress.Risk.Szca.ToastSessionExpired);
    return null;
  }

  if (!pState.id && pending.busId) pState.id = pending.busId;
  await restoreKeyPairToPState(pState, pending);
  return applySzcaCertificate(pending.certDn, pState.keyPair, context);
};

/** SZCA 完成后调 findCertDown，由后端判断是否认证成功 */
const handleSzcaReturn = async (): Promise<SzcaReturnResult> => {
  consoleLog('[SZCA] handleSzcaReturn start', { href: window.location.href, query: location.query }, 'all');
  // URL 上的 certificateSerialNumber 单独落盘，不与 certSn 混用
  resolveSzcaBusId();
  captureCertificateSerialFromQuery(pState.id);

  const pending = loadSzcaPendingState();
  consoleLog('[SZCA] handleSzcaReturn pending', pending, 'all');
  if (!pending) return 'failed';

  await restoreKeyPairToPState(pState, pending);

  const certInfo = (await findCertDown(pending.busId, pending.bizNo, pending.resId)) as {
    certSn?: string | null;
    descInfo?: string;
    retCode?: string;
  };
  consoleLog('[SZCA] findCertDown response', certInfo, 'all');

  if (certInfo?.certSn) {
    persistSzcaCert(pending.certDn || pState.certDn, certInfo.certSn, {
      privateKeyPem: pending.privateKeyPem,
      publicKeyPem: pending.publicKeyPem,
    });
    clearSzcaPendingState();
    return 'ok';
  }

  if (String(certInfo?.retCode) === SZCA_AUTH_IN_PROGRESS_RETCODE) {
    return 'need_reapply';
  }

  showToast(certInfo?.descInfo || i18n.OpenProgress.Risk.Szca.ToastCertFetchFailed);
  return 'failed';
};

/** 整页跳转打开 SZCA 人脸；完成后由 videoCallbackBusUrl 回跳本站（ca=1） */
const openSzcaFaceAuthWithPrepared = (prepared: SzcaPrepareResult) => {
  persistMainlandOpenAccountToken();
  consoleLog('[SZCA] openSzcaFaceAuthWithPrepared -> redirect', { authUrl: prepared.authUrl, certDn: prepared.certDn, bizNo: prepared.bizNo, resId: prepared.resId }, 'all');
  window.location.href = prepared.authUrl;
};

/** findCertDown 成功或 need_reapply 时重试人脸 */
const completeSzcaReturn = async (context: SzcaAuthContext): Promise<boolean> => {
  const result = await handleSzcaReturn();
  if (result === 'ok') return true;
  if (result === 'need_reapply') {
    const prepared = await retrySzcaCertificateApply(context);
    if (!prepared) return false;
    openSzcaFaceAuthWithPrepared(prepared);
    return false; // 已整页离开，回跳后再走 tryCompleteSzcaReturn
  }
  return false;
};

export const isSzcaReturnForContext = (context: SzcaAuthContext) => {
  if (!isSzcaReturnFromQuery()) return false;
  if (context === 'editRefusedRisk') {
    return location.query.mode === 'editRefused' && location.query.phase === 'risk';
  }
  return location.query.mode !== 'editRefused';
};

export const shouldHandleSzcaReturn = (context: SzcaAuthContext) => {
  if (context === 'editRefusedRisk') {
    return pState.editRefusedMode && pState.editRefusedPhase === 'risk';
  }
  return !pState.editRefusedMode;
};

/**
 * 发起完整 SZCA 流程：申请证书 → 人脸 → findCertDown。
 * 返回 true 表示 certSn 已就绪，可继续 CA 签或下一步。
 */
export async function runSzcaAuthFlow(opts: RunSzcaAuthFlowOptions = {}): Promise<boolean> {
  const context = opts.context ?? 'riskInfo';
  const force = opts.force ?? false;

  if (pState.uploadLicForm.idCardType !== 'MAINLAND_CHINA') return true;

  if (force) {
    // 仅清内存，强制重申；localStorage 保留至 finishOpenAccount
    pState.certSn = '';
    pState.certDn = '';
  } else {
    hydrateSzcaCertToPState(pState);
    if (pState.certSn && !hasSzcaPendingSession()) return true;
  }

  if (isSzcaReturnForContext(context) && hasSzcaPendingSession()) {
    return completeSzcaReturn(context);
  }

  const prepared = await prepareSzcaFaceAuth(context, force);
  if (!prepared) return false;

  openSzcaFaceAuthWithPrepared(prepared);
  return false; // 已整页离开，回跳带 ca=1 后再走 findCertDown
}

export type RunSzcaShortAuthFlowOptions = {
  certBizNo: string;
  resId?: string;
  context?: SzcaAuthContext;
};

/**
 * 资料未变更时的 CA 短流程：themeAcquire → findCertDown（跳过 certificateApply / 人脸）。
 * findCertDown 返回认证进行中（retCode -5）时，强制完整重申（certificateApply + 人脸）。
 * 返回 true 表示 certSn 已就绪。
 */
export async function runSzcaShortAuthFlow(opts: RunSzcaShortAuthFlowOptions): Promise<boolean> {
  if (pState.uploadLicForm.idCardType !== 'MAINLAND_CHINA') return true;

  // 短流程仍以接口为准重新拉取；先清空内存，避免误用过期 certSn
  pState.certSn = '';

  const themeData = await themeAcquire(pState.uploadLicForm.idCardNo, pState.uploadLicForm.fullName);

  if (!themeData?.certDN) {
    showToast(i18n.OpenProgress.Risk.Video.ToastServiceError);
    return false;
  }

  pState.certDn = themeData.certDN;

  const certInfo = (await findCertDown(pState.id, opts.certBizNo, opts.resId)) as {
    certSn?: string | null;
    descInfo?: string;
    retCode?: string;
  };

  if (certInfo?.certSn) {
    let pems: { privateKeyPem?: string; publicKeyPem?: string } | undefined;
    if (pState.keyPair) {
      try {
        const { exportKeyPairPem } = await loadCert();
        const exported = exportKeyPairPem(pState.keyPair as Parameters<typeof exportKeyPairPem>[0]);
        pems = { privateKeyPem: exported.privateKeyPem, publicKeyPem: exported.publicKeyPem };
      } catch {
        /* 短流程可能无本地密钥 */
      }
    }
    persistSzcaCert(themeData.certDN, certInfo.certSn, pems);
    return true;
  }

  // 短流程无 pending 密钥，认证进行中只能走完整重申
  if (String(certInfo?.retCode) === SZCA_AUTH_IN_PROGRESS_RETCODE) {
    return runSzcaAuthFlow({ context: opts.context ?? 'riskInfo', force: true });
  }

  showToast(certInfo?.descInfo || i18n.OpenProgress.Risk.Szca.ToastCertFetchFailed);
  return false;
}

/**
 * 页面 onMount：URL 带 ca=1 时完成 findCertDown。
 * 返回 true 表示认证已成功完成。
 *
 * 回跳路径禁止用缓存旧 certSn 冒充「本次」成功（人脸失败后 pending 丢失时会误放行）。
 */
export async function tryCompleteSzcaReturn(opts: TryCompleteSzcaReturnOptions = {}): Promise<boolean> {
  const context = opts.context ?? 'riskInfo';

  if (!shouldHandleSzcaReturn(context)) return false;
  if (pState.uploadLicForm.idCardType !== 'MAINLAND_CHINA') return false;
  if (!isSzcaReturnForContext(context)) return false;

  // 回跳带 serial 时先单独落盘；pState.id 未恢复时用 pending.busId
  resolveSzcaBusId();
  captureCertificateSerialFromQuery(pState.id);

  if (!hasSzcaPendingSession()) {
    showToast(i18n.OpenProgress.Risk.Szca.ToastSessionExpired);
    return false;
  }

  const ok = await completeSzcaReturn(context);
  if (ok) {
    await opts.onSuccess?.();
  }
  return ok;
}
